import { useMemo, useState, useRef, useEffect } from "react";
import Button from "../../../../../../../../../../../components/Button/Button";
import CalendarIcon from "../../../../../../../../../../../assets/svg/calendar-icon";
import ThreeDotsVerticalIcon from "../../../../../../../../../../../assets/svg/three-dots-vertical-icon";
import { AgentAvailabilityPopUp } from "./component/AgentAvailabilityPopUp/AgentAvailabilityPopUp";
import { LeavesShimmer } from "./component/LeavesShimmer";
import { LeaveActionPopUp } from "./component/LeaveActionPopUp";

import { format, differenceInCalendarDays, parseISO } from "date-fns";
import { AgentAvailabilityLeavesProps, Leave } from "../../utils/types";

export const AgentAvailabilityLeaves = ({
  initialAgentBreaksData,
  isLoadingAgentBreaksData,
}: AgentAvailabilityLeavesProps) => {
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [activeLeaveId, setActiveLeaveId] = useState<string | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const handleAddUnavailability = () => {
    setIsDateRangeModalOpen((prev) => !prev);
  };

  const handleLeaveAction = (leaveId: string) => {
    setActiveLeaveId(activeLeaveId === leaveId ? null : leaveId);
  };

  const handleClosePopup = () => {
    setActiveLeaveId(null);
  };

  const upcomingLeaves = useMemo<Leave[]>(() => {
    if (!initialAgentBreaksData?.upcoming_breaks) return [];

    return [...initialAgentBreaksData.upcoming_breaks]
      .sort(
        (a, b) =>
          new Date(a?.start_date).getTime() - new Date(b?.start_date).getTime()
      )
      .map((breakItem) => {
        const startDate = parseISO(breakItem?.start_date);
        const endDate = parseISO(breakItem?.end_date);
        const days = differenceInCalendarDays(endDate, startDate) + 1;

        return {
          id: breakItem?.id,
          startDate: format(startDate, "d MMM yyyy"),
          endDate: format(endDate, "d MMM yyyy"),
          start_date: breakItem?.start_date,
          end_date: breakItem?.end_date,
          days,
        };
      });
  }, [initialAgentBreaksData]);

  const previousLeaves = useMemo<Leave[]>(() => {
    if (!initialAgentBreaksData?.previous_breaks) return [];

    return [...initialAgentBreaksData.previous_breaks]
      .sort(
        (a, b) =>
          new Date(a?.start_date).getTime() - new Date(b?.start_date).getTime()
      )
      .map((breakItem) => {
        const startDate = parseISO(breakItem?.start_date);
        const endDate = parseISO(breakItem?.end_date);
        const days = differenceInCalendarDays(endDate, startDate) + 1;

        return {
          id: breakItem?.id,
          startDate: format(startDate, "d MMM yyyy"),
          endDate: format(endDate, "d MMM yyyy"),
          start_date: breakItem?.start_date,
          end_date: breakItem?.end_date,
          days,
        };
      });
  }, [initialAgentBreaksData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        const target = event.target as HTMLElement;
        if (!target.closest("[data-leave-action-button]")) {
          setActiveLeaveId(null);
        }
      }
    };

    if (activeLeaveId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeLeaveId]);

  return (
    <div className="relative pb-3">
      <div className="text-md text-verba-field-label dark:text-verba-field-label-dk mb-1">
        Mark leaves
      </div>
      <Button
        Icon={CalendarIcon}
        iconLeft={true}
        iconClass="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk"
        innerText="Add Unavailability"
        buttonContainerClass="w-[264px] border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk py-2 rounded-lg"
        innerTextClass="text-verba-blue dark:text-verba-blue-dk text-md font-medium"
        onClick={handleAddUnavailability}
      />

      {isLoadingAgentBreaksData ? (
        <>
          <LeavesShimmer listLength={upcomingLeaves?.length} />
          <div className="mt-6">
            <LeavesShimmer listLength={previousLeaves?.length} />
          </div>
        </>
      ) : (
        <>
          {upcomingLeaves?.length > 0 && (
            <div className="mt-9">
              <div className="flex items-center gap-1 mb-3 bg-pure-white dark:bg-pure-white-dk">
                <span className="text-md text-verba-field-label dark:text-verba-field-label-dk">
                  Upcoming Leaves
                </span>
                <span className="flex items-center justify-center px-[3.33px] py-[1.25px] rounded-sm text-xxs font-semibold text-verba-indigo dark:text-verba-indigo-dk border border-gray-100 dark:border-gray-100-dk">
                  {upcomingLeaves?.length}
                </span>
              </div>

              <div className="flex flex-col gap-3">
                {upcomingLeaves?.map((leave) => (
                  <div key={leave?.id}>
                    <div className="relative w-max flex items-center py-[9px] px-3 border border-border-gray dark:border-border-gray-dk rounded-lg">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-4.5 h-4.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
                        <span className="text-md font-medium text-verba-field-label dark:text-verba-field-label-dk">
                          {leave?.startDate} - {leave?.endDate}
                        </span>
                      </div>
                      <div className="w-6 h-px mx-1.5 bg-border-gray dark:bg-border-gray-dk" />
                      <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk mr-6">
                        {leave?.days} day
                        {leave?.days && leave?.days > 1 ? "s" : ""}
                      </span>
                      <button
                        onClick={() => handleLeaveAction(leave?.id)}
                        className="w-[26px] h-[26px] flex items-center justify-center rounded-md hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
                        aria-label="Leave actions"
                        data-leave-action-button
                      >
                        <ThreeDotsVerticalIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
                      </button>

                      {activeLeaveId === leave?.id && (
                        <LeaveActionPopUp
                          popupRef={popupRef}
                          leaveId={leave?.id}
                          startDate={leave?.start_date}
                          endDate={leave?.end_date}
                          onClose={handleClosePopup}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {previousLeaves?.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center gap-1 mb-3 bg-pure-white dark:bg-pure-white-dk">
                <span className="text-md text-verba-field-label dark:text-verba-field-label-dk">
                  Previous Leaves
                </span>
                <span className="flex items-center justify-center px-[3.33px] py-[1.25px] rounded-sm text-xxs font-semibold text-verba-indigo dark:text-verba-indigo-dk border border-gray-100 dark:border-gray-100-dk">
                  {previousLeaves?.length}
                </span>
              </div>

              <div className="flex flex-col gap-3">
                {previousLeaves?.map((leave) => (
                  <div key={leave?.id}>
                    <div className="relative w-max flex items-center py-[9px] px-3 border border-border-gray dark:border-border-gray-dk rounded-lg">
                      <div className="flex items-center gap-1">
                        <CalendarIcon className="w-4.5 h-4.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
                        <span className="text-md font-medium text-verba-field-label dark:text-verba-field-label-dk">
                          {leave?.startDate} - {leave?.endDate}
                        </span>
                      </div>
                      <div className="w-6 h-[1px] mx-1.5 bg-border-gray dark:bg-border-gray-dk" />
                      <span className="text-xs font-medium text-gray-placeholder dark:text-gray-placeholder-dk mr-6">
                        {leave?.days} day
                        {leave?.days && leave?.days > 1 ? "s" : ""}
                      </span>
                      <button
                        onClick={() => handleLeaveAction(leave?.id)}
                        className="w-[26px] h-[26px] flex items-center justify-center rounded-md hover:bg-hover-effect dark:hover:bg-hover-effect-dk transition-colors"
                        aria-label="Leave actions"
                        data-leave-action-button
                      >
                        <ThreeDotsVerticalIcon className="w-3.5 h-3.5 text-gray-placeholder dark:text-gray-placeholder-dk" />
                      </button>

                      {activeLeaveId === leave?.id && (
                        <LeaveActionPopUp
                          popupRef={popupRef}
                          leaveId={leave?.id}
                          startDate={leave?.start_date}
                          endDate={leave?.end_date}
                          onClose={handleClosePopup}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <AgentAvailabilityPopUp
        open={isDateRangeModalOpen}
        onClose={() => setIsDateRangeModalOpen(false)}
        initialAgentBreaksData={initialAgentBreaksData}
      />
    </div>
  );
};
